
precision highp float;
precision highp sampler2D;

in vec3 position; // we only use the x coordinate, but three.js requires a vec3

// Uniforms for the particle shader
// Width/height of the particle state texture in texels (`maxAmount`); maps vertex index → UV.
uniform float rttSize;
// Latest GPGPU pass: encoded particle positions (RGBA packed coords + parity bit in R).
uniform sampler2D rttTexture;

// Second MRT: normalized scalar field magnitude (R); decoded in VS as fieldScalar.
uniform sampler2D rttScalarTexture;

// Snapshot of positions at RTT tick start; used with rttTexture to compute motion `speed`.
uniform sampler2D rttTexturePrev;

// Screen size in pixels.
uniform float screenSize;

// Map canvas area in px^2 (`width * height`); with `PARTICLE_DENSITY` caps how many particle
// indices are drawn (see vertex shader density test).
uniform float particleDensityArea;

// Frame interval in seconds; multiplies position delta for motion `speed`.
uniform float renderStepSize;

// When true, point size scales slightly with motion `speed` (vertex shader).
uniform bool fastIsLarger;

// Screen-space motion approx px/s between RTT frames;
out float speed;
// Decoded field magnitude at particle: rttScalar R × PARTICLE_FIELD_MAGNITUDE_MAX.
out float fieldScalar;

/**
 * Computes the current and previous positions of a particle and the difference between them,
 * for animated points driven by a decoded vector field.
 * Also calculates the "speed" of the particle for use in size/color encoding.
 *
 * @param position_ The 1D index of the particle; used for mapping to the 2D texture coordinates.
 * @returns vec2(pos): The new position (x,y).
 */
vec2 advance(float position_) {
    // If this particle exceeds the density threshold (i.e., not used), output zero speed and dummy data.
    if (position_ > (PARTICLE_DENSITY / 1000.0) * particleDensityArea) {
        speed = 0.0;
        fieldScalar = 0.0;
        return vec2(0.0);
    }

    // Calculate normalized texture coordinates from the 1D particle index.
    // this is used to read the particle position from the rtt texture
    float x = fract(position_ / rttSize);
    float y = floor(position_ / rttSize) / rttSize;

    // Read encoded particle data from current RTT
    vec4 color = texture(rttTexture, vec2(x, y));

    // Combine for precise position, this is a hack
    // ba contains the integer part of the position
    // rg contains the fractional part of the position
    vec2 pos = color.ba + color.rg / 255.0; // Combine for precise position

    // Do the same for previous frame texture, to compute movement.
    vec4 colorPrev = texture(rttTexturePrev, vec2(x, y));
    vec2 posPrev = colorPrev.ba + colorPrev.rg / 255.0;

    // Compute vector difference (motion) between frames.
    vec2 diff = pos - posPrev;

    // Calculate speed as a function of frame time and moved distance, in screen units.
    // Protects against division by zero when particle is stationary.
    speed = (renderStepSize * length(diff * screenSize));

    float scalarNorm = texture(rttScalarTexture, vec2(x, y)).r;
    fieldScalar = scalarNorm * PARTICLE_FIELD_MAGNITUDE_MAX;

    // Return the new position of the particle
    return pos;
}

void main() {
  // Compute the next position of the particle
  vec2 pos = advance(position.x);

  // Set the rendered particle's size. Bigger if fast, fixed if not. (if)
  gl_PointSize = fastIsLarger 
    ? (PARTICLE_SIZE * log(speed + 0.5)) / 2.0
    : PARTICLE_SIZE;
    
  // Map normalised [0,1] positions to clip space [-1,1], flipping y for screen orientation.
  gl_Position = vec4(
    2.0 * pos.x - 1.0,
    - (2.0 * pos.y - 1.0),
    0.0,
    1.0
  );
}
